import ActionState from "@/types/sv-data/events/ActionState";
import { RoutingGroup } from "../types";

/**
 * Event queue sort keys. Values are the `sortBy` sent to the `/eventqueue` endpoint and
 * MUST mirror the server-side `EventQueueSortBy` enum (Data service). Appended members
 * keep existing positional values stable.
 */
export enum EventQueueSortBy {
	Default = 0,
	Created = 1,
	Area = 2,
	Event = 3,
	Priority = 4,
	AlarmCount = 5,
	Region = 6,
	EventId = 7
}

export interface EventsQueueState {
	AlarmQueueFilters: AlarmQueueFilter[];
	FilteredEvents: FilteredEvent[];
	FilteredEventIds: number[];
	CurrentSort: {
		desc: boolean;
		changed: boolean;
		sort: number;
		reversed?: boolean;
	};
	maxEventsToShow: number;
	activeAlarmCount: number;
	AlarmQueueFilterIDs: AlarmQueueFilterID[];
	// Note: we should really consider merging this property with AlarmQueueFilters
	EventsLastUpdated: number;
	HoverEvent: MouseEvent;
	DisplayedEvent: FilteredEvent;
	AlarmActiveFilter: AlarmQueueFilterID;
	ManualPatrolShown: boolean;
	NewFilterModalShown: boolean;
	ActivityLogShown: boolean;
	ManualActionShown: boolean;
	SiteTree: SiteTreeNode[];
	ChangedTreeNodes: SiteTreeNode[];
	treeSelectedGroup: Group | any;
	selectedGroups: Group[];
	searchSelectedGroup: Group | any;
	queueMapBounds: google.maps.LatLngBoundsLiteral;
	eventCountsByType: EventCountsByType;
	eventQueueGetOverride: string;
	processingError: string | null;
	RoutingGroups: RoutingGroup[];
	AlarmTags: AlarmTag[];
	userZone: Group | any;
	lastEventHandled: number | null;
	ServerEventTypes: ServerEventType[];
	filteredServerEventTypes: ServerEventType[] | null;
	HandlingEvent: boolean;
	advancedTreeSearchCancelToken: any;
	JoiningEventID: any;
	TourNoteRequired: boolean;
	hideMapFlag: boolean;
	lostConnectionCount: number;
	eventQueueUpdateInfoHistory: EventQueueInfo[],
	debugMode: boolean;
	pendingEventPickup: PendingEventPickup,
	selectedGroup: Group,
	peekEventId: number | null,
	bulkHandleShown: boolean,
	virtualOperatorControlPanelShown: boolean,
	allAlarmsSelected: boolean,
	selectedAlarms: number[];
	refreshQueue: boolean;
	closingAction: ActionState;
	eventQueuePriorityColors: EventQueuePriorityColors[];
	lastSubGroupId: number | null;
	eventQueueAllowanceResult: EventQueueAllowanceResult;
}

export interface EventCountsByType {
	parked: number;
	patrols: number;
	inProcess: number;
	onTest: number;
}

export interface PendingEventPickup {
	eventQueueItem?: FilteredEvent,
	highestPriorityAlarm?: boolean,
	currentFilterID?: number,
	allowInProcessing: boolean,
	openInNewWindow: boolean,
	pickupMethod: string
}

export interface EventQueueInfo
{
	CurrentUTC: string,
	EventsLastUpdate: string,
	AlarmList: string,
	ParkedList: string,
	TourList: string
}

export interface AlarmQueueFilterID {
	filterId: number;
	regionId: number;
}

export interface AlarmQueueFilter {
	// Properties pulled in from the server
	alarmQueueFilterID: number;
	title: string;
	priorityHigherThan?: number;
	priorityHigherThanDDL?: string;
	numberOfEvents?: number;
	numberOfEventsDDL?: string;
	routingGroupID?: number;
	responseAlarmTypeIds: number[];
	olderThan?: number;
	olderThanDDL?: string;
	userID?: number;
	shared?: boolean;
	created?: string;
	combinedFilters?: boolean;
	bounds?: {
		anyBounds: boolean;
		north: number;
		south: number;
		west: number;
		east: number;
	};
	groups?: any[];
	regionID: number;
	regionTitle?: string;
	regionalFilterID: string;

	// Computed properties
	isActive?: boolean;
	isLocal?: boolean;
	activeAlarmCount?: number;
	hide?: boolean;
	addedRegionTitle?: boolean;
}

export interface FilteredEvent {
	age: number;
	alarmCount: number;
	alarmTypeID: null;
	awaitingRestoreCount: number;
	created: string;
	displayTitle: string;
	eventID: number;
	eventTitle: string;
	eventTypeID: number;
	externalSystemInfo: null;
	groupCreated: string;
	groupID: number;
	groupTitle: string;
	groupUnparkAt: null;
	inFilters: null;
	inProcessing: boolean;
	isCCure: boolean;
	isTrouble: boolean;
	lastActivity: null;
	lastUnparkedAt: string;
	location: string;
	needsSilence: boolean;
	priority: number;
	raisedByVideowall: boolean;
	regionID: number;
	regionTitle: string;
	regionURL: string;
	responseID: null;
	responseTitle: string;
	routingGroupColourCode: string;
	routingGroupID: number;
	routingGroupTitle: string;
	timezone: null;
	unParkAt: null;
	userID: null;
	userUnparkAt: null;
	username: string;
	viewedByVideowall: boolean;
	priorityStyle: string;
	slaBreachAt: Date | null;
	slaBreachRaised: boolean | null;
	isMaxEventsExceededEvent?: boolean | null;
}

/**
 * Used by the filter editor when creating new alarm queue filters.
 */
export interface AlarmQueueFilterEditModel {
	alarmQueueFilterID?: number;
	title: string;
	priorityValue?: number;
	priorityOperator?: string;
	numberOfEventsValue?: number;
	numberOfEventsOperator?: string;
	responseAlarmTypeIds: number[];
	alarmAgeValue?: number;
	alarmAgeOperator?: string;
	shared?: boolean;
	created?: string;
	combinedFilters?: boolean;
	groupID?: number | null;
	inherited?: boolean;
	excluded?: boolean;
	groups?: AlarmQueueFilterGroup[];
}

export interface AlarmQueueFilterGroup {
	alarmQueueFilterGroupsID: number;
	alarmQueueFilterID: number;
	groupID: number;
	inherited: boolean;
}

export interface AlarmTag {
	responseAlarmTypeID: number;
	title: string;
	isIncident: boolean;
	alwaysRaise: boolean;
	groupId: number;
	groupTitle: string;
	tenantId: number;
}

export interface Group {
	groupID?: number;
	parentGroupID?: number | null;
	groupTypeID?: number;
	selected?: boolean;
	timeZoneID?: number;
	title?: string;
	state?: string;
	armed?: boolean;
	toggleArmAt?: Date;
	maskDurationLimit?: number;
}

export interface SiteTreeNode {
	group?: Group;
	subGroups?: SiteTreeNode[];
	isOpen?: boolean;
	show?: boolean;
	isOpenDefault?: boolean | null;
	contents?: any;
	contentsOpen?: boolean;
	loadingContents?: boolean;
	node?: SiteTreeNode;
}

export interface ServerEventType {
	serverTypeId: number;
	eventNum: number;
	title: string;
	isDefault: boolean;
	description: string;
	eventCode: string;
	restoreCode: string;
	restorable: boolean;
	restored: boolean;
	incident: boolean;
	input: boolean;
	system: boolean;
	defaultAutoHandle: boolean;
	defaultPriority: number;
	serverTypeEventProtocolTypeId: number;
	input1Label: string;
	input2Label: string;
	defaultIgnore: boolean;
	ignoreDefaultAlarm: boolean;
	defaultTag: number;
	defaultScript: number;
	matchRadius: number;
	matchGeofence: boolean;
}

export interface LoadServerEventTypesParams {
	serverType: number;
	groupID: number | null;
	pageSize?: number | null;
	regionId?: number | null;
}

export interface SearchServerEventTypesParams extends LoadServerEventTypesParams {
	filter: string;
}

export interface EventQueuePriorityColors {
	eventQueuePriorityColorId: number;
	backgroundColor: string;
	foregroundColor: string;
	minPriority?: number;
	maxPriority?: number;
}

export interface EventQueuePriorityColorsExample extends EventQueuePriorityColors {
	examplePriority?: string;
}

export interface EventQueueAllowanceResult {
	eventQueueAllowanceThreshold: number;
	eventQueueAllowanceResetThreshold: number;
}